import { isEqual } from 'lodash';
const isPromise = <T>(value: unknown): value is Promise<T> =>
  Boolean(value) && typeof value === 'object' && value !== null && 'then' in value && typeof value.then === 'function';

type PendingPromise = Promise<unknown> | null;
type SingletonPromiseFn<TParams, TResult> = {
  bivarianceHack(params?: TParams): Promise<TResult>;
}['bivarianceHack'];

const pendingPromises: Record<string, Map<unknown, PendingPromise>> = {};

/**
 * Stores active promises by name and params to avoid duplicate requests.
 */
function useSingletonPromise<TParams = unknown, TResult = unknown>(
  name: string,
  fn: SingletonPromiseFn<TParams, TResult>,
) {
  const loadData = async (params?: TParams) => {
    const keyForParams = params ?? 'default';
    const pendingPromisesByName = pendingPromises[name] || new Map<unknown, PendingPromise>();
    let dataPromise: PendingPromise = null;

    pendingPromisesByName.forEach((value, key) => {
      if (isEqual(keyForParams, key)) {
        dataPromise = value;
      }
    });

    const response = isPromise<TResult>(dataPromise) ? dataPromise : fn(params);

    if (!isPromise<TResult>(dataPromise)) {
      pendingPromises[name] = pendingPromisesByName.set(keyForParams, response);
    }

    return response.then(result => {
      pendingPromises[name] = pendingPromisesByName.set(keyForParams, null);

      return result as TResult;
    });
  };

  return loadData;
}

export default useSingletonPromise;
